import React from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment } from "@fortawesome/free-regular-svg-icons";

import { Tooltip } from "components/tooltip/tooltip";
import { useModal } from "shared/hooks/use-modal";
import { useAttributeFeedback } from "shared/hooks/api/use-attribute-feedback";
import { useSaveAttributeFeedback } from "shared/hooks/api/use-save-attribute-feedback";
import { AttributeFeedbackModal } from "./components/attribute-feedback-modal";
import { buildCreateFeedbackPayload, buildUpdateFeedbackPayload, isAttributeFeedbackSupported } from "./lib/helpers";

import "./attribute-feedback.scss";

export const AttributeFeedback = ({ attr, taskType, taskKey, canEdit, tooltipPosition = "bottom" }) => {
    const { open, onOpen, onClose } = useModal();
    const isSupported = isAttributeFeedbackSupported(attr, taskType);
    const { data: feedbackByAttribute, isSuccess } = useAttributeFeedback(taskKey, isSupported);
    const { mutate: saveFeedback, isLoading: isSaving } = useSaveAttributeFeedback(taskKey);
    const feedback = feedbackByAttribute?.[attr.uuid] ?? null;
    const isReadOnly = !canEdit;

    if (!feedback && !canEdit) {
        return;
    }

    if (!isSupported || !isSuccess) {
        return null;
    }

    const handleSubmit = (values) => {
        const save = feedback
            ? { id: feedback.id, payload: buildUpdateFeedbackPayload(values) }
            : { payload: buildCreateFeedbackPayload({ attributeUuid: attr.uuid, values }) };

        saveFeedback(save, { onSuccess: onClose });
    };

    const tooltip = isReadOnly ? "View feedback" : feedback ? "Edit feedback" : "Provide feedback";

    return (
        <>
            <Tooltip
                position={tooltipPosition}
                text={tooltip}
            >
                <button
                    type="button"
                    aria-label={tooltip}
                    className={classnames("button -small attribute-feedback-button", { "-has-feedback": !!feedback })}
                    onClick={onOpen}
                >
                    <FontAwesomeIcon icon={faComment} />
                </button>
            </Tooltip>
            {open && (
                <AttributeFeedbackModal
                    attributeName={attr.name}
                    feedback={feedback}
                    isSaving={isSaving}
                    isReadOnly={isReadOnly}
                    onClose={onClose}
                    onSubmit={handleSubmit}
                />
            )}
        </>
    );
};
